import { useState } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { BaseSelectedOptionsProps } from './MultiSelectDropdown.types';
import { searchCombo } from 'api/fragments';
import { transformExitRulesResponse } from 'modules/journey/components/ExitRules/ExitRules.utils';
import { RuleTypeEnum } from 'modules/workflows/components/ExitRuleSlideOver/ExitRuleSliderOver.constants';
import { normalizeComparatorType } from 'modules/workflows/components/ExitRuleSlideOver/ExitRuleSlideOver.utils';

export const GET_EXIT_RULES = gql`
      ${searchCombo}
  query getExitRules {
    getExitRules {
      ... on JourneyExitRulePair{
        exitRule {
       ... on ConversionMetExitRule {
          id
          eventName
         
          searchQuery {
            ...SearchComboFragment
            __typename
          }
          __typename
        }
        ... on CustomEventExitRule {
          id
          eventName
          searchQuery {
            ...SearchComboFragment
          }
        }
        ... on SystemEventExitRule {
          id
          eventType
          searchQuery {
            ...SearchComboFragment
          }
        }
        ... on UserPropertyExitRule {
          id
          userField
          searchQuery {
            ...SearchComboFragment
          }
        }

        ... on ExitRuleCombo {
          id
          combinator
         
          rules {
            ... on CustomEventExitRule {
              id
              eventName
              searchQuery {
                ...SearchComboFragment
              }
            }
            ... on SystemEventExitRule {
              id
              eventType
              searchQuery {
                ...SearchComboFragment
              }
            }
            ... on UserPropertyExitRule {
              id
              userField
              searchQuery {
                ...SearchComboFragment
              }
            }
          }
        }
      }
      }
    }
}`;

const FETCH_POLICY = 'cache-and-network';

export const getExitRuleText = (exitRule: any) => {
  // it's a property type, we need to get the first search query field and value
  // for everything else we need to get the event name if it exists on the rule, if not we need to get the event and value from the child rule.
  const { ruleType } = exitRule;
  if (ruleType === RuleTypeEnum.UserPropertyRule) {
    const { field, value, comparatorType } = exitRule?.searchQuery?.searchQueries?.[0] || {};
    if (field) {
      return `${field} ${normalizeComparatorType(comparatorType)} ${value}`;
    } else {
      const { userField } = exitRule;
      return `${userField} occurs`;
    }
  } else {
    const { eventName } = exitRule;
    const { field, value, comparatorType } = exitRule?.searchQuery?.searchQueries?.[0] || {};
    if (field && value && comparatorType) {
      return `${field} ${normalizeComparatorType(comparatorType)} ${value}`;
    } else if (eventName) return `${eventName} occurs`;
    else {
      return '';
    }
  }
};

export const constructSecondaryText = (exitRule: any) => {
  const transformed = transformExitRulesResponse({ exitRules: [exitRule] });
  const rules = transformed?.rules;

  const rule = rules?.[0];
  if (rule?.childRules.length === 0) {
    return '';
  }
  const [firstRule, ...restRules] = rule?.childRules || [];
  const [_, ...restSearchQueries] = firstRule.searchQuery?.searchQueries || [];

  const text = getExitRuleText(firstRule);
  const additioanlRuleCount = (restRules || []).reduce((acc, curr) => {
    const searchQueries = curr?.searchQuery?.searchQueries || [];
    return acc + searchQueries.length || 0;
  }, restSearchQueries?.length || 0);
  const ruleCount = additioanlRuleCount > 0 ? ` + ${additioanlRuleCount} other criteria` : '';
  return text + ruleCount;
};

const buildOption = (exitRuleId: string, secondaryText = '') => ({
  id: secondaryText, // this is for the secondary text
  name: exitRuleId,
  key: exitRuleId,
  value: exitRuleId,
});

export const useGetExitRulesSelectOptions = ({
  defaultOptions,
  values,
}: BaseSelectedOptionsProps) => {
  const [hasError, setHasError] = useState(false);
  const [options, setOptions] = useState(
    defaultOptions?.map((x) => buildOption(x.id, x.name)) || [],
  );

  const hydrateOptions = (data: any) => {
    const hydratedOptions = data?.getExitRules?.map((x: any) => {
      const exitRuleId = x?.exitRule?.id;

      const secondaryText = constructSecondaryText(x?.exitRule);

      return buildOption(exitRuleId, secondaryText);
    });
    const sorted = hydratedOptions.sort((a: any, b: any) => {
      const aPriority = values?.includes(a.name) ? 0 : 1;
      const bPriority = values?.includes(b.name) ? 0 : 1;

      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      return 0; // Maintain original order for non-prioritized items
    });

    setOptions(sorted);
  };

  const [getExitRules, { loading: exitRulesLoading }] = useLazyQuery(GET_EXIT_RULES, {
    fetchPolicy: FETCH_POLICY,
    onError: () => setHasError(true),
    onCompleted: (data) => {
      hydrateOptions(data);
      setHasError(false);
    },
  });

  /* -------------------- configuration -------------------- */
  const runQuery = (opts: any) => {
    if (GET_EXIT_RULES) {
      getExitRules(opts);
    }
  };

  return {
    hasError,
    options,
    runQuery,
    isLoading: exitRulesLoading,
    fieldName: 'Exit rules',
    selectPlaceHolder: 'Tip: Paste ID from journey',
  };
};
